import { FC, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Player } from "@lottiefiles/react-lottie-player";
import { useNavigate } from "react-router-dom";
import { useExerciseHandlers } from "@/utils/hooks/useExerciseHandlers";
import { renderExerciseTypePot } from "@/utils/exercises";
import { useModalAction, useModalState } from "@/contexts/modal/Context";
import { ExerciseOptionTypes, Submission } from "@/types";
import {
  useExerciseState,
  useExerciseAction,
} from "@/contexts/exercise/Context";
import routes from "@/config/routes.ts";
import alien1 from "@/assets/animations/alien1.json";
import alien2 from "@/assets/animations/alien2.json";
import alien3 from "@/assets/animations/alien3.json";

export const calculateAverageScore = (
  exerciseResults: Array<{ score: number; total: number }>,
) => {
  if (!exerciseResults || exerciseResults.length === 0) {
    return 0;
  }

  const totalScore = exerciseResults?.reduce(
    (acc, curr) => acc + curr.score,
    0,
  );

  const totalQuestions = exerciseResults?.reduce(
    (acc, curr) => acc + curr.total,
    0,
  );

  if (totalQuestions === 0) return 0;

  return (totalScore / totalQuestions) * 10;
};

interface Data {
  submissionData: Submission[];
  score: number;
  total: number;
  exerciseType: ExerciseOptionTypes;
}

interface ScoreProps {
  score: number;
  total: number;
  percentage?: number;
  exerciseType: string;
  submissionData?: Submission[];
}

interface ScoreBarProps {
  score: number;
  total: number;
  type: ExerciseOptionTypes;
}

const Score: FC<ScoreProps> = ({ score, total, percentage }) => {
  return (
    <>
      <div className="mt-3 text-center leading-[117.5%] text-brand">
        Your score is{" "}
        <span className="font-semibold">
          {score} out of {total}
        </span>{" "}
        ({percentage}%)
      </div>
    </>
  );
};

const ScoreBar: React.FC<ScoreBarProps> = ({ score, total, type }) => {
  const progressPercentage = ((score / total) * 100).toFixed(0);

  const barColorClass =
    score / total >= 0.8
      ? "bg-[#219653]"
      : score / total >= 0.5
      ? "bg-[#FF8A00]"
      : "bg-[#C80505]";

  const textColorClass =
    score / total >= 0.8
      ? "text-[#219653]"
      : score / total >= 0.5
      ? "text-[#FF8A00]"
      : "text-[#C80505]";

  const formattedType = renderExerciseTypePot(type);

  return (
    <>
      <div className="flex items-center gap-8">
        <div className="text-xl font-semibold text-brand-secondary min-w-[170px]">
          {formattedType}
        </div>

        <div>
          <div className="leading-[117.5%] text-brand">
            Your score is{" "}
            <span className="font-semibold">
              {score} out of {total}
            </span>{" "}
            ({Math.round((score / total) * 100)}%)
          </div>

          <div className="flex gap-3 mt-3 font-medium text-center whitespace-nowrap">
            <div
              className={`shrink-0 my-auto h-[9px] rounded-[100px] min-w-[266px] relative bg-opacity-20 ${barColorClass}`}
            >
              <div
                style={{ width: `${progressPercentage}%` }}
                className={`h-[9px] rounded-[100px] ${barColorClass}`}
              />
            </div>

            <div className={`${textColorClass}`}>
              {score}/{total}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const ExerciseV2Modal: FC<ScoreProps> = () => {
  const navigate = useNavigate();
  const { data } = useModalState();
  const { closeModal } = useModalAction();
  const { exerciseResults, totalExercises } = useExerciseState();
  const { nextExercise, updateExerciseResult, endSequence } =
    useExerciseAction();
  const { handleUpdateExerciseStatus, handleSaveProgress } =
    useExerciseHandlers();

  const { submissionData, score, total, exerciseType } = data as Data;

  const percentage = Math.round((score / total) * 100);

  const isLastExercise = exerciseResults.length >= totalExercises - 1;

  const [viewResults, setViewResults] = useState(false);

  const handleNextExerciseClick = async () => {
    const newResult = { type: exerciseType, score, total };

    const resultExists = exerciseResults.some((r) => r?.type === exerciseType);

    if (!resultExists) {
      updateExerciseResult(newResult);
    }

    try {
      if (isLastExercise) {
        await handleSaveProgress(
          submissionData,
          exerciseType,
          newResult,
          calculateAverageScore(exerciseResults),
        );

        await handleUpdateExerciseStatus("completed");

        setViewResults(true);
      } else {
        await handleSaveProgress(submissionData, exerciseType, newResult, 0);

        await handleUpdateExerciseStatus("in progress");

        closeModal?.();
        nextExercise();
      }
    } catch (error) {
      console.error("Failed to process exercise results:", error);
    }
  };

  const handleClose = () => {
    endSequence();
    closeModal();
    navigate(routes.exercises);
  };

  const message =
    percentage <= 20
      ? "You can do better! C'mon!"
      : percentage <= 90
      ? "You weren't focused enough!"
      : "Well done!";

  const animation =
    percentage <= 20 ? alien1 : percentage <= 90 ? alien2 : alien3;

  return (
    <div>
      <div className="flex items-stretch flex-col gap-4 justify-between h-full">
        <div className="flex flex-col h-full justify-center items-center">
          {viewResults ? (
            exerciseResults.map((r, i) => (
              <div key={r.type + i} className="mt-3">
                <ScoreBar
                  type={r.type as ExerciseOptionTypes}
                  score={r.score}
                  total={r.total}
                />
              </div>
            ))
          ) : (
            <>
              <div className="text-xl leading-5 text-center text-brand-secondary font-semibold">
                <Player
                  loop
                  autoplay
                  src={animation}
                  style={{ height: "200px", width: "200px" }}
                />
                {message}
              </div>

              <Score
                score={score}
                total={total}
                percentage={percentage}
                exerciseType=""
              />
            </>
          )}

          <footer>
            <Button
              size="default"
              variant={
                isLastExercise && viewResults
                  ? "default"
                  : isLastExercise
                  ? "outline"
                  : "default"
              }
              onClick={
                isLastExercise && viewResults
                  ? handleClose
                  : handleNextExerciseClick
              }
              className={`px-12 py-3 mt-11 border ${
                isLastExercise && viewResults
                  ? ""
                  : isLastExercise
                  ? "bg-white text-brand-secondary border-brand-secondary"
                  : " border-brand"
              }`}
            >
              {isLastExercise && viewResults
                ? "Close"
                : isLastExercise
                ? "View results"
                : "Next exercise"}
            </Button>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default ExerciseV2Modal;
